var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _class, _class2;

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }

/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { Component, html, Prop } from '@sick-davinci/ce-decorators';
import { AbstractBarChart } from '../AbstractBarChart.js';
import * as QuickChart from '@meistercharts/meistercharts';
import { CssTools } from '../../_utils/CssTools.js';
import { BarChartGroupedLayout } from './BarChartGroupedLayout.js';
import { computeThresholds } from '../../_utils/Thresholds.js';
import { LegendGrid } from '../../legend/LegendGrid.js';
/**
 * A base class for grouped bar charts
 */

export let AbstractBarChartGrouped = (_dec = Component({
  tag: 'davinci-abstract-bar-chart-grouped',
  abstract: true,
  dependencies: [LegendGrid]
}), _dec2 = Reflect.metadata("design:paramtypes", [Object]), _dec3 = Prop({
  reflectAsAttribute: false
}), _dec4 = Reflect.metadata("design:type", Object), _dec5 = Prop({
  reflectAsAttribute: false
}), _dec6 = Reflect.metadata("design:type", Number), _dec(_class = _dec2(_class = (_class2 = class AbstractBarChartGrouped extends AbstractBarChart {
  /**
   * The api reference that is used to delegate the calls
   */
  constructor(seriesColors) {
    super(seriesColors);

    _defineProperty(this, "_thresholds", []);
  }

  /**
   * The thresholds to be shown
   */
  get thresholds() {
    return this._thresholds;
  }

  set thresholds(value) {
    this._thresholds = value;

    this._update();
  }
  /**
   * The width of the value-axis (in rem)
   *
   * @deprecated use {@link axisWidth} instead
   *
   * @default ValueAxisBuilder.AXIS_DEFAULT_WIDTH
   */


  get valueAxisWidth() {
    return this.axisWidth;
  }

  set valueAxisWidth(value) {
    this.axisWidth = value;
  }

  _init() {
    var _this$shadowRoot;

    if (!this._api) {
      return;
    }

    const holder = (_this$shadowRoot = this.shadowRoot) === null || _this$shadowRoot === void 0 ? void 0 : _this$shadowRoot.getElementById('holder');

    if (!holder) {
      return;
    }

    holder.appendChild(this._api.holder); // initial update

    this._update();
  }
  /**
   * See base class
   */


  componentFirstRender() {
    this._init();
  }

  componentConnected() {
    super.componentConnected();
    this._api = this.createApi(); // this could be a reattach without a following call for componentFirstRender -> reestablish QuickChart API

    this._init();
  }
  /**
   * Creates the API
   */


  createApi() {
    return QuickChart.createBarChartGrouped();
  }

  componentDisconnected() {
    super.componentDisconnected(); // We have been detached from the DOM -> clean up

    if (this._api) {
      var _this$shadowRoot2, _this$shadowRoot2$get;

      (_this$shadowRoot2 = this.shadowRoot) === null || _this$shadowRoot2 === void 0 ? void 0 : (_this$shadowRoot2$get = _this$shadowRoot2.getElementById('holder')) === null || _this$shadowRoot2$get === void 0 ? void 0 : _this$shadowRoot2$get.removeChild(this._api.holder);

      this._api.dispose();
    }

    delete this._api;
  }
  /**
   * See base class
   */


  render() {
    var _this$legendLabels;

    // either show chart with surrounding legend or not
    if (((_this$legendLabels = this.legendLabels) === null || _this$legendLabels === void 0 ? void 0 : _this$legendLabels.length) > 0) {
      const colors = Array.from(new Set(this._getSeriesColors()));
      return html`
      <davinci-legend
        placement="N"
        small-font
        compact
        symbols='["box"]'
        symbol-size="${CssTools.REM2PIXEL(.7)}"
        .labels=${this.legendLabels}
        .palette=${this.palette}
        .colors=${colors}>
        ${this._getHolder()}
      </davinci-legend>
      `;
    } else {
      return html`${this._getHolder()}`;
    }
  }
  /**
   * @override
   */


  _update() {
    if (this._api === undefined) {
      return;
    }

    const hostFontFamily = this._getHostFontFamily();

    if (!hostFontFamily) {
      return;
    }

    const data = this._createData();

    const style = this._createStyle(hostFontFamily);

    this._api.setData(data);

    this._api.setStyle(style);
  }
  /**
   * @override
   */


  _createStyle(hostFontFamily) {
    const style = super._createStyle(hostFontFamily);

    style.thresholds = computeThresholds(this.thresholds, !this.horizontal, hostFontFamily, this.shorthandLabels, this.decimalPlaces);
    style.barSize = CssTools.REM2PIXEL(BarChartGroupedLayout.BAR_SIZE);
    style.barGap = CssTools.REM2PIXEL(BarChartGroupedLayout.BAR_GAP);
    style.minGapBetweenGroups = CssTools.REM2PIXEL(BarChartGroupedLayout.MIN_GROUP_DISTANCE);
    style.maxGapBetweenGroups = CssTools.REM2PIXEL(BarChartGroupedLayout.MAX_GROUP_DISTANCE);
    style.contentViewportMargin = {
      right: this.horizontal ? CssTools.REM2PIXEL(1.5) : 0
    };
    style.valueAxisStyle = this._computeValueAxisStyle(this.horizontal, hostFontFamily); // disable overflow-indicator

    style.overflowIndicatorStyle = {
      arrowHeadLength: 0,
      arrowHeadWidth: 0
    };
    return style;
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "thresholds", [_dec3, _dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "thresholds"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "valueAxisWidth", [_dec5, _dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "valueAxisWidth"), _class2.prototype)), _class2)) || _class) || _class);
